// GENERAL PARAMETERS

export const ENVS = {
  DEV: 'dev',
  STAGING: 'staging',
  PROD: 'production',
} as const

// BUSINESS PARAMETERS

export const ADMIN_AGENCY: string =
  import.meta.env.VITE_APP_ADMIN_AGENCY || 'unknown_value'

export const PRODUCTS_TYPES = {
  L1_PANEL_CLASS_DIGITAL: 'l1panelclassDigital',
  L1_PANEL_CLASS_STATIC: 'l1panelclassStatic',
  L1_PANEL_CLASS: 'l1panelclass',
  L2_PRODUCT: 'l2product',
  L3_SUBPRODUCT: 'l3subproduct',
  L4: 'l4',
  L5: 'l5',
  L6: 'l6',
  L7: 'l7',
  L8: 'l8',
  L9: 'l9',
  IS_DIGITAL: 'isDigital',
} as const

export type ProductType = (typeof PRODUCTS_TYPES)[keyof typeof PRODUCTS_TYPES]

export function getProductType(type: string): ProductType {
  let _type: ProductType
  if (type === PRODUCTS_TYPES.L1_PANEL_CLASS) {
    _type = PRODUCTS_TYPES.L1_PANEL_CLASS
  } else if (type === PRODUCTS_TYPES.L2_PRODUCT) {
    _type = PRODUCTS_TYPES.L2_PRODUCT
  } else if (type === PRODUCTS_TYPES.L3_SUBPRODUCT) {
    _type = PRODUCTS_TYPES.L3_SUBPRODUCT
  } else if (type === PRODUCTS_TYPES.L4) {
    _type = PRODUCTS_TYPES.L4
  } else if (type === PRODUCTS_TYPES.L5) {
    _type = PRODUCTS_TYPES.L5
  } else if (type === PRODUCTS_TYPES.L6) {
    _type = PRODUCTS_TYPES.L6
  } else if (type === PRODUCTS_TYPES.L7) {
    _type = PRODUCTS_TYPES.L7
  } else if (type === PRODUCTS_TYPES.L8) {
    _type = PRODUCTS_TYPES.L8
  } else if (type === PRODUCTS_TYPES.L9) {
    _type = PRODUCTS_TYPES.L9
  } else if (type === PRODUCTS_TYPES.IS_DIGITAL) {
    _type = PRODUCTS_TYPES.IS_DIGITAL
  } else {
    throw new Error('Unknown product type')
  }
  return _type
}

// Per-country i18n labels for the panel-type hierarchy (L1…L9). Keyed by
// country id (as a string) with a `default` fallback.
type PanelTypeLabels = {
  L1_PANEL_CLASS: string
  L2_PRODUCT: string
  L3_SUBPRODUCT: string
  L4: string
  L5: string
  L6: string
  L7: string
  L8: string
  L9: string
}

export const I18N_PANEL_TYPES: Record<string, PanelTypeLabels> = {
  default: {
    L1_PANEL_CLASS: 'l1panelclass',
    L2_PRODUCT: 'l2product',
    L3_SUBPRODUCT: 'l3subproduct',
    L4: 'l4subproduct',
    L5: 'l5subproduct',
    L6: 'l6subproduct',
    L7: 'l7subproduct',
    L8: 'l8subproduct',
    L9: 'l9subproduct',
  },
  239: {
    // Norwey
    L1_PANEL_CLASS: 'Environment',
    L2_PRODUCT: 'Environment sub-type',
    L3_SUBPRODUCT: 'Proposed Package Group 3',
    L4: 'Proposed Package Main',
    L5: 'Format',
    L6: 'Installation Type',
    L7: 'Plan No.',
    L8: 'Panel Type',
    L9: 'Revenue Branch',
  },
  37: {
    // Denmark
    L1_PANEL_CLASS: 'Environment',
    L2_PRODUCT: 'Environment sub-type',
    L3_SUBPRODUCT: 'Proposed Package Group 3',
    L4: 'Proposed Package Main',
    L5: 'Format',
    L6: 'Installation Type',
    L7: 'Plan No.',
    L8: 'Panel Type',
    L9: 'Revenue Branch',
  },
  198: {
    // Sweden
    L1_PANEL_CLASS: 'Environment',
    L2_PRODUCT: 'Environment sub-type',
    L3_SUBPRODUCT: 'Proposed Package Group 3',
    L4: 'Proposed Package Main',
    L5: 'Format',
    L6: 'Installation Type',
    L7: 'Plan No.',
    L8: 'Panel Type',
    L9: 'Revenue Branch',
  },
  61: {
    // France
    L1_PANEL_CLASS: 'Environnement',
    L2_PRODUCT: "Sous-type d'environnement",
    L3_SUBPRODUCT: 'Univers Communication',
    L4: 'Support Communication',
    L5: 'Type Panel',
    L6: 'Nb de faces réels',
    L7: 'Univers Contractuel',
    L8: 'Region CCF',
    L9: 'Format',
  },
}

export const AUDIENCES_CONFIG = {
  index: 100,
  percent: 20,
  maxElementsPerGroup: 10,
} as const

export const AUDIENCES_TYPE = {
  INDEX: 'index',
  PERCENT: 'percent',
} as const

export const EDITING_STATUS = {
  CLOSED: 'CLOSED',
  OPENED_FULL: 'OPENED_FULL',
  OPENED_MINIMIZED: 'OPENED_MINIMIZED',
  FULLSCREEN_VIEWER: 'FULLSCREEN_VIEWER',
  FULLSCREEN_EDITOR: 'FULLSCREEN_EDITOR',
} as const

export const getRedirectUri = (): string => {
  return `${window.location.origin}/login`
}

export const getRequireComponent = (): Record<string, unknown> => {
  return import.meta.glob('./components/common/global/**/[A-Z]*.vue', {
    eager: true,
  })
}

export const SessionStorageKeys = {
  PlanUrl: 'oyj24fi987d',
} as const
